import { createHashRouter, Navigate } from "react-router-dom";
import AuthLayout from "src/components/pages/Auth/AuthLayout/AuthLayout";
import Header from "src/components/shared/Header/Header";
import UserHistory from "src/components/shared/UserHistory/UserHistory";
import Generator from "src/generator/Generator";
import { GeneratorV2DevPage } from "src/generatorV2/GeneratorV2DevPage";
import { useIsDev } from "src/hooks/useIsDev";
import ForgotPassword from "src/pages/Auth/ForgotPassword/ForgotPassword";
import Login from "src/pages/Auth/Login/Login";
import Register from "src/pages/Auth/Register/Register";
import ResetPassword from "src/pages/Auth/ResetPassword/ResetPassword";
import EntryHistory from "src/pages/EntryHistory/EntryHistory";
import ErrorPage from "src/pages/ErrorPage";
import ExportEntry from "src/pages/Exports/ExportEntry/ExportEntry";
import Exports from "src/pages/Exports/Exports";
import HelpVideos from "src/pages/HelpVideos/HelpVideos";
import Collection from "src/pages/Home/Collection/Collection";
import CustomDashboardPage from "src/pages/Home/CustomDashboardPage";
import Home from "src/pages/Home/Home";
import LogbookPreview from "src/pages/LogbookPreview/LogbookPreview";
import ApiKeys from "src/pages/Management/ApiKeys/ApiKeys";
import Clients from "src/pages/Management/Clients/Clients";
import Organizations from "src/pages/Management/Clients/Organizations/Organizations";
import Yacht from "src/pages/Management/Clients/Organizations/Yachts/Yacht/Yacht";
import Yachts from "src/pages/Management/Clients/Organizations/Yachts/Yachts";
import CrewRoles from "src/pages/Management/CrewRoles/CrewRoles";
import Logbooks from "src/pages/Management/Logbooks/Logbooks";
import Management from "src/pages/Management/Management";
import Permissions from "src/pages/Management/Permissions/Permissions";
import EditSubscription from "src/pages/Management/Subscriptions/EditSubscription";
import Subscriptions from "src/pages/Management/Subscriptions/Subscriptions";
import Users from "src/pages/Management/Users/Users";
import UserTypes from "src/pages/Management/UserTypes/UserTypes";
import NotFound from "src/pages/NotFound";
import Organize from "src/pages/Organize/Organize";
import PrivacyPolicy from "src/pages/PrivacyPolicy/PrivacyPolicy";
import AddReports from "src/pages/Reports/AddReport/AddReport";
import Report from "src/pages/Reports/Report/Report";
import Reports from "src/pages/Reports/Reports";
import SearchPage from "src/pages/SearchPage/SearchPage";
import AccountDetails from "src/pages/Settings/AccountDetails/AccountDetails";
import DSSCalendarPage from "src/pages/Settings/AdvancedFeatures/DSSCalendar/DSSCalendar";
import LogbookSigners from "src/pages/Settings/AdvancedFeatures/LogbookSigners/LogbookSigners";
import LogbookSignersHistory from "src/pages/Settings/AdvancedFeatures/LogbookSigners/LogbookSignersHistory";
import Backup from "src/pages/Settings/Backup/Backup";
import Certificates from "src/pages/Settings/Certificates/Certificates";
import CrewSetUp from "src/pages/Settings/CrewSetUp/CrewSetUp";
import UserForm from "src/pages/Settings/CrewSetUp/UserForm/UserForm";
import DesktopApp from "src/pages/Settings/DesktopApp/DesktopApp";
import LogbookManager from "src/pages/Settings/LogbookManager/LogbookManager";
import ChecklistLogbook from "src/pages/Settings/Miscellaneous/CheckLists/ChecklistLogbook";
import CheckLists from "src/pages/Settings/Miscellaneous/CheckLists/CheckLists";
import DraftEntries from "src/pages/Settings/Miscellaneous/DraftEntries/DraftEntries";
import MyDocuments from "src/pages/Settings/MyDocuments/MyDocuments";
import NMEA from "src/pages/Settings/NMEA/NMEA";
import SettingsCardsPage from "src/pages/Settings/SettingsCardsPage";
import SystemSettings from "src/pages/Settings/SystemSettings/SystemSettings";
import BiofoulingSetUp from "src/pages/Settings/YachtSetup/BiofoulingSetUp/BiofoulingSetUp";
import CabinArrangement from "src/pages/Settings/YachtSetup/CabinArrangement/CabinArrangement";
import DynamicPositioningSetup from "src/pages/Settings/YachtSetup/DynamicPositioningSetup/DynamicPositioningSetup";
import Fgas from "src/pages/Settings/YachtSetup/Fgas/Fgas";
import GmdssSetup from "src/pages/Settings/YachtSetup/GmdssSetup/GmdssSetup";
import LiftingEquipment from "src/pages/Settings/YachtSetup/LiftingEquipment/LiftingEquipment";
import LiftingEquipmentHistory from "src/pages/Settings/YachtSetup/LiftingEquipment/LiftingEquipmentHistory";
import MedicalHistory from "src/pages/Settings/YachtSetup/MedicalHistory/MedicalHistory";
import UsersMedicalHistory from "src/pages/Settings/YachtSetup/MedicalHistory/UsersMedicalHistory";
import OzoneDepletingSubstances from "src/pages/Settings/YachtSetup/OzoneDepletingSubstances/OzoneDepletingSubstances";
import TankPlans from "src/pages/Settings/YachtSetup/TankPlans/TankPlans";
import UnCodeAutomation from "src/pages/Settings/YachtSetup/UnCodeAutomation/UnCodeAutomation";
import YachtSetup from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup";
import YachtSetupHistory from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetupHistory/YachtSetupHistory";
import Audits from "src/pages/Summary/Audits/Audits";
import RequestedComments from "src/pages/Summary/Audits/RequestedComments";
import ReviewComments from "src/pages/Summary/Audits/ReviewComments/ReviewComments";
import ViewAuditEntry from "src/pages/Summary/Audits/ViewAuditEntry/ViewAuditEntry";
import RevAudCreate from "src/pages/Summary/RevAudCreate/RevAudCreate";
import RevAudReport from "src/pages/Summary/RevAudReport/RevAudReport";
import Review from "src/pages/Summary/Reviews/Review/Review";
import ViewReviewEntry from "src/pages/Summary/Reviews/Review/ViewReviewEntry/ViewReviewEntry";
import Reviews from "src/pages/Summary/Reviews/Reviews";
import Summary from "src/pages/Summary/Summary";
import VerifyEntries from "src/pages/Summary/VerifyEntries/VerifyEntries";

import HistoricalSnapshot from "../pages/HistoricalSnapshot/HistoricalSnapshot";
import CrewTransfer from "../pages/Management/CrewSetUp/CrewTransfer/CrewTransfer";
import FleetCrewSetUp from "../pages/Management/CrewSetUp/FleetCrewSetUp";
import AddVesselReport from "../pages/Management/Reports/AddReport/AddVesselReport";
import VesselReports from "../pages/Management/Reports/VesselReports";
import FleetYachts from "../pages/Management/Yachts/Yachts";
import GenericLogins from "../pages/Settings/GenericLogins/GenericLogins";
import GenericLoginsEdit from "../pages/Settings/GenericLogins/GenericLoginsEdit/GenericLoginsEdit";
import Labels from "../pages/Settings/Miscellaneous/Labels/Labels";
import UsersMedStatements from "../pages/Settings/YachtSetup/MedicalHistory/UsersMedStatements";
import ShipsStamps from "../pages/Settings/YachtSetup/ShipsStamps/ShipsStamps";
import AppProvider from "./AppProvider";
import ProtectedRoute from "./ProtectedRoute/ProtectedRoute";

function DevRoute() {
  const isDev = useIsDev();
  if (!isDev) return <Navigate to="/" replace />;
  return (
    <>
      <Header />
      <GeneratorV2DevPage />
    </>
  );
}

const router = createHashRouter([
  {
    element: <AppProvider />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: "auditor",
            children: [
              {
                path: "login",
                element: <Login auditor />,
              },
              {
                path: "register",
                element: <Register />,
              },
            ],
          },
          {
            path: "login",
            element: <Login />,
          },
          {
            path: "forgot-password",
            element: <ForgotPassword />,
          },
          {
            path: "reset-password",
            element: <ResetPassword />,
          },
          {
            path: "new-user-set-up",
            element: <ResetPassword newUser />,
          },
        ],
      },
      {
        path: "privacy-policy",
        element: <PrivacyPolicy />,
      },

      {
        path: "dev/generator-v2",
        element: <DevRoute />,
      },

      {
        element: <ProtectedRoute />,
        errorElement: (
          <>
            <Header />
            <ErrorPage />
          </>
        ),
        children: [
          {
            path: "",
            element: <Navigate to="/home" />,
          },
          {
            path: "exports",
            children: [
              { path: "", element: <Exports /> },
              { path: "entry/:meid", element: <ExportEntry /> },
            ],
          },
          {
            path: "home",
            element: <Home />,
          },

          {
            path: "collection/:collectionId",
            element: <Collection />,
          },
          {
            path: "collection/:lbid/custom-dashboard",
            element: <CustomDashboardPage />,
          },
          {
            path: "organize-logbooks",
            element: <Organize />,
          },

          {
            path: "logbook/:logbookId/:idType/:lbeidOrPartId?/:token?",
            element: <Generator />,
          },
          {
            path: "logbook-preview/:logbookId/:idType",
            element: <LogbookPreview />,
          },
          {
            path: "historical-snapshot/:logbookId",
            element: <HistoricalSnapshot />,
          },
          {
            path: "historical-snapshot/:logbookId/:aid",
            element: <HistoricalSnapshot review />,
          },

          {
            path: "logbook/:hid/history",
            element: <EntryHistory />,
          },
          {
            path: "help-videos",
            element: <HelpVideos />,
          },
          {
            path: "search/:collectionId?",
            element: <SearchPage />,
          },
          {
            path: "management",
            children: [
              { path: "", element: <Management /> },
              { path: "api-keys", element: <ApiKeys /> },
              { path: "permissions", element: <Permissions /> },
              { path: "logbooks", element: <Logbooks /> },
              { path: "user-types", element: <UserTypes /> },
              { path: "crew-roles", element: <CrewRoles /> },
              {
                path: "crew-set-up",
                children: [
                  { path: "", element: <FleetCrewSetUp /> },
                  { path: "crew-transfer", element: <CrewTransfer /> },
                ],
              },
              {
                path: "yachts",
                children: [
                  { path: "", element: <FleetYachts /> },
                  { path: "yacht/:yachtid", element: <Yacht isFleet /> },
                ],
              },
              {
                path: "reports",
                children: [
                  { path: "", element: <VesselReports /> },
                  { path: "add-report", element: <AddVesselReport /> },
                ],
              },
              {
                path: "subscriptions",
                children: [
                  { path: "", element: <Subscriptions /> },
                  {
                    path: "edit-subscription/:yachtid/:clientid",
                    element: <EditSubscription />,
                  },
                ],
              },
              {
                path: "users",
                children: [
                  { path: "", element: <Users /> },
                  {
                    path: "edit-user/:userId",
                    element: <UserForm isManagement />,
                  },
                  {
                    path: "create-user",
                    element: <UserForm isManagement isCreateUser />,
                  },
                ],
              },
              {
                path: "clients",
                children: [
                  { path: "", element: <Clients /> },
                  {
                    path: "organizations/:clid",
                    children: [
                      { path: "", element: <Organizations /> },
                      {
                        path: "yachts/:orgid",
                        children: [
                          { path: "", element: <Yachts /> },
                          { path: "yacht/:yachtid", element: <Yacht /> },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "settings",
            children: [
              { path: "", element: <SettingsCardsPage /> },
              {
                path: "account",
                element: <AccountDetails />,
              },
              {
                path: "backup",
                element: <Backup />,
              },
              {
                path: "files",
                element: <MyDocuments />,
              },
              {
                path: "gen",
                children: [
                  {
                    path: "",
                    element: <GenericLogins />,
                  },
                  {
                    path: "add-login",
                    element: <GenericLoginsEdit newLogin />,
                  },
                  {
                    path: "edit-login/:genid",
                    children: [
                      { path: "", element: <GenericLoginsEdit /> },
                      {
                        path: "history",
                        element: (
                          <UserHistory requestUrl="get-generic-history" />
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: "yacht",
                children: [
                  {
                    path: "",
                    element: (
                      <SettingsCardsPage section="yacht" sect="_yacht" />
                    ),
                  },
                  {
                    path: "particulars",
                    element: <YachtSetup />,
                  },
                  {
                    path: "stamps",
                    element: <ShipsStamps />,
                  },
                  {
                    path: "cabins",
                    element: <CabinArrangement />,
                  },
                  {
                    path: "gmdss",
                    element: <GmdssSetup />,
                  },
                  {
                    path: "dps",
                    element: <DynamicPositioningSetup />,
                  },
                  {
                    path: "biofouling",
                    element: <BiofoulingSetUp />,
                  },
                  {
                    path: "tankplans",
                    element: <TankPlans />,
                  },
                  {
                    path: "ozone",
                    element: <OzoneDepletingSubstances />,
                  },
                  {
                    path: "fgas",
                    element: <Fgas />,
                  },
                  {
                    path: "lifting",
                    element: <LiftingEquipment />,
                  },
                  {
                    path: "medlog",
                    children: [
                      {
                        path: "",
                        element: <MedicalHistory />,
                      },
                      {
                        path: "view-history/:userid",
                        children: [
                          {
                            path: "",
                            element: <UsersMedicalHistory />,
                          },
                          {
                            path: "medical-statements",
                            children: [
                              {
                                path: "",
                                element: <UsersMedStatements />,
                              },
                              {
                                path: "history/:cardid",
                                element: <UsersMedStatements history />,
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "unCodes",
                    element: <UnCodeAutomation />,
                  },
                  {
                    path: "history/lifting",
                    element: <LiftingEquipmentHistory />,
                  },
                  {
                    path: "history/:section",
                    element: <YachtSetupHistory />,
                  },
                ],
              },

              {
                path: "userlist",
                children: [
                  { path: "", element: <CrewSetUp /> },
                  {
                    path: "create-user",
                    element: <UserForm isCreateUser />,
                  },
                  {
                    path: "edit-user/:userId",
                    children: [
                      { path: "", element: <UserForm /> },
                      {
                        path: "history",
                        element: <UserHistory requestUrl="get-user-hist" />,
                      },
                    ],
                  },
                ],
              },

              {
                path: "certs",
                element: <Certificates />,
              },
              {
                path: "NMEA",
                element: <NMEA />,
              },
              {
                path: "logman",
                element: <LogbookManager />,
              },
              {
                path: "usersettings",
                element: <SystemSettings />,
              },
              {
                path: "app",
                element: <DesktopApp />,
              },
              {
                path: "advanced",
                children: [
                  { path: "dssCalendar", element: <DSSCalendarPage /> },
                  {
                    path: "",
                    element: (
                      <SettingsCardsPage section="advanced" sect="_advanced" />
                    ),
                  },
                  {
                    path: "signers",
                    element: <LogbookSigners />,
                  },
                  {
                    path: "history/signers",
                    element: <LogbookSignersHistory />,
                  },
                ],
              },
              {
                path: "misc",
                children: [
                  {
                    path: "",
                    element: <SettingsCardsPage section="misc" sect="_misc" />,
                  },
                  { path: "drafts", element: <DraftEntries /> },
                  {
                    path: "checklists",
                    children: [
                      {
                        path: "collection/:collectionId",
                        element: <CheckLists />,
                      },
                      {
                        path: "",
                        element: <CheckLists />,
                      },
                      {
                        path: "logbook/:collectionId/:lbid",
                        element: <ChecklistLogbook />,
                      },
                      {
                        path: "logbook/:lbid",
                        element: <ChecklistLogbook />,
                      },
                    ],
                  },
                  {
                    path: "labels",
                    element: <Labels />,
                  },
                ],
              },
            ],
          },

          {
            path: "reports",
            children: [
              { path: "", element: <Reports /> },
              { path: "report/:reportId", element: <Report /> },
              {
                path: "add-report",
                element: <AddReports />,
              },
              {
                path: "edit-report/:rid",
                element: <AddReports />,
              },
            ],
          },

          {
            path: "summary",
            children: [
              { path: "", element: <Summary /> },
              {
                path: "reviews",
                children: [
                  { path: "", element: <Reviews /> },
                  { path: "create-review", element: <RevAudCreate /> },
                  {
                    path: "review/:aid",
                    children: [
                      {
                        path: "full-report/:yachtid",
                        element: <RevAudReport />,
                      },
                      {
                        path: ":idType/:lbeidOrPartId",
                        element: <ViewReviewEntry />,
                      },
                      { path: "", element: <Review /> },
                    ],
                  },
                ],
              },
              {
                path: "reports",
                element: <Reports />,
              },
              {
                path: "audits",
                children: [
                  { path: "", element: <Audits /> },
                  { path: "create-audit", element: <RevAudCreate /> },
                  {
                    path: "audit/:aid",
                    children: [
                      {
                        path: "full-report/:yachtid",
                        element: <RevAudReport />,
                      },
                      {
                        path: "requested-comments",
                        element: <RequestedComments />,
                      },
                      {
                        path: ":idType/:lbeidOrPartId",
                        element: <ViewAuditEntry />,
                      },
                      { path: "", element: <Review audit /> },
                    ],
                  },
                  { path: "review-comments", element: <ReviewComments /> },
                ],
              },
              {
                path: "verify-entries/:idType/:lbeidOrPartId",
                element: <VerifyEntries />,
              },
            ],
          },

          {
            path: "*",
            element: <NotFound />,
          },
        ],
      },
    ],
  },
]);

export default router;
